import { useEffect, useRef } from "react";

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
] as const;

/**
 * Signs the user out after `timeoutMs` of no interaction. Any activity (mouse,
 * key, touch, scroll) resets the countdown, so an active session never expires;
 * only true inactivity past the timeout returns the user to the login page.
 * Timer resets are throttled to once/second to avoid churn on mousemove.
 */
export function useIdleLogout(
  enabled: boolean,
  timeoutMs: number,
  onIdle: () => void,
): void {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return;

    let timer: number;
    let lastReset = 0;

    const reset = () => {
      const now = Date.now();
      if (now - lastReset < 1000) return;
      lastReset = now;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => onIdleRef.current(), timeoutMs);
    };

    const opts: AddEventListenerOptions = { passive: true };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, reset, opts));
    reset();

    return () => {
      window.clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, reset, opts));
    };
  }, [enabled, timeoutMs]);
}
